import { Trophy, CheckCircle2, Circle, Clock } from 'lucide-react'
import { Card, CardContent } from '@/components/ui/card'
import { Badge } from '@/components/ui/badge'
import { cn } from '@/lib/utils'
import { Link } from 'react-router-dom'

/* Bracket tree view — absolute-positioning approach.
   Elke ronde is een kolom met vaste containerhoogte (2^totalRounds * MATCH_H).
   Match op index i binnen ronde met N matches krijgt center-y op
   (i + 0.5) * (containerHeight / N). Zo staan matches in ronde 2 exact
   midden tussen hun twee R1-parents, en connectors kunnen recht getrokken
   worden via SVG absolute over de hele container. */

const MATCH_H = 140     // vaste match-card hoogte — genoeg voor 2 team-rijen + score, geen overlap
const COL_W = 280       // kolom-breedte — comfortabel voor typische team-namen
const COL_GAP = 56      // horizontale ruimte tussen kolommen (voor connectors)

export default function BracketTreeView({ matches, onMatchClick, isAdmin, tone = 'acid', participantHrefs = {} }) {
  // Consolation-bracket krijgt peach-tint zodat main + losers-bracket visueel
  // niet meer identiek zijn.
  const connectorRgb = tone === 'peach' ? 'var(--peach-rgb)' : 'var(--acid-rgb)'
  const headerColor = tone === 'peach' ? 'text-peach' : 'text-acid'
  const hoverBorder = tone === 'peach' ? 'hover:border-peach' : 'hover:border-acid'
  if (!matches || matches.length === 0) {
    return (
      <div className="text-center py-12 text-muted-foreground">
        <Trophy className="h-16 w-16 mx-auto mb-4 opacity-50" />
        <p>Nog geen bracket. Start het toernooi om er één te genereren.</p>
      </div>
    )
  }

  // 3e-plaats-match uit de main-tree halen — die krijgt een eigen kaart
  // onder de finale-kolom zodat de bracket-lijnen kloppen.
  const thirdPlaceMatch = matches.find(m => m.is_third_place_match)
  const mainMatches = matches.filter(m => !m.is_third_place_match)

  const byRound = mainMatches.reduce((acc, m) => {
    (acc[m.round] ||= []).push(m); return acc
  }, {})
  const rounds = Object.keys(byRound).sort((a, b) => +a - +b).map(Number)
  const totalRounds = Math.max(...rounds)
  const firstRoundMatches = (byRound[rounds[0]] || []).length
  // Meer ademruimte: elke R1 match krijgt zone van 1.6× card-hoogte.
  const containerH = Math.max(firstRoundMatches, 2) * MATCH_H * 1.6

  const roundName = (r) => {
    const fromEnd = totalRounds - r
    if (fromEnd === 0) return 'Finale'
    if (fromEnd === 1) return 'Halve finale'
    if (fromEnd === 2) return 'Kwartfinale'
    if (fromEnd === 3) return '1/8 finale'
    return `Ronde ${r}`
  }

  const matchCenter = (roundIdx, matchIdx) => {
    const n = byRound[rounds[roundIdx]].length
    return ((matchIdx + 0.5) * containerH) / n
  }

  return (
    <div className="overflow-x-auto pb-6 pt-14 pl-1">
      <div
        className="relative"
        style={{
          width: rounds.length * COL_W + (rounds.length - 1) * COL_GAP,
          height: containerH,
        }}
      >
        {/* Ronde-headers bovenaan — grotere titel + subtitel */}
        {rounds.map((r, i) => (
          <div key={`h-${r}`}
               className="absolute -top-12"
               style={{ left: i * (COL_W + COL_GAP), width: COL_W }}>
            <h3 className={cn('text-base font-bold tracking-tight', headerColor)}>{roundName(r)}</h3>
            <div className="text-[11px] text-muted-foreground font-mono uppercase tracking-widest mt-0.5">
              Ronde {r} · {byRound[r].length} match{byRound[r].length === 1 ? '' : 'es'}
            </div>
          </div>
        ))}

        {/* SVG connectors — één laag onder de kaarten. */}
        <svg className="absolute inset-0 pointer-events-none"
             width={rounds.length * COL_W + (rounds.length - 1) * COL_GAP}
             height={containerH}>
          {rounds.slice(0, -1).map((r, roundIdx) => {
            const roundMatches = byRound[r].slice().sort((a, b) => a.match_number - b.match_number)
            const x1 = roundIdx * (COL_W + COL_GAP) + COL_W
            const x2 = (roundIdx + 1) * (COL_W + COL_GAP)
            const xMid = (x1 + x2) / 2
            return roundMatches.map((m, mi) => {
              const y1 = matchCenter(roundIdx, mi)
              const y2 = matchCenter(roundIdx + 1, Math.floor(mi / 2))
              return (
                <path key={`c-${m.id}`}
                      d={`M ${x1} ${y1} L ${xMid} ${y1} L ${xMid} ${y2} L ${x2} ${y2}`}
                      stroke={`rgb(${connectorRgb} / 0.35)`}
                      strokeWidth="1.5"
                      fill="none" />
              )
            })
          })}
        </svg>

        {/* Match-kaarten — absolute per (ronde, index). */}
        {rounds.map((r, roundIdx) => {
          const roundMatches = byRound[r].slice().sort((a, b) => a.match_number - b.match_number)
          const x = roundIdx * (COL_W + COL_GAP)
          return roundMatches.map((m, mi) => {
            const y = matchCenter(roundIdx, mi) - MATCH_H / 2
            return (
              <div key={m.id}
                   className="absolute"
                   style={{ left: x, top: y, width: COL_W, height: MATCH_H }}>
                <BracketMatchCard match={m} onClick={() => onMatchClick && onMatchClick(m)}
                                  isAdmin={isAdmin} hoverBorder={hoverBorder}
                                  participantHrefs={participantHrefs} />
              </div>
            )
          })
        })}
      </div>

      {/* 3e-plaats match — aparte kaart onder de tree, met eigen kopje.
         Blijft visueel losstaand van de hoofdbracket omdat het geen
         doorschuif-vervolg heeft. */}
      {thirdPlaceMatch && (
        <div className="mt-12 pt-8 pl-1 border-t border-gold/30">
          <h3 className="text-base font-bold text-gold tracking-tight">3e-plaats match</h3>
          <div className="text-[11px] text-muted-foreground font-mono uppercase tracking-widest mt-0.5 mb-3">
            Halve-finale verliezers · Brons
          </div>
          <div style={{ width: COL_W, height: MATCH_H }}>
            <BracketMatchCard match={thirdPlaceMatch}
                              onClick={() => onMatchClick && onMatchClick(thirdPlaceMatch)}
                              isAdmin={isAdmin} hoverBorder="hover:border-gold"
                              participantHrefs={participantHrefs} />
          </div>
        </div>
      )}
    </div>
  )
}

function BracketMatchCard({ match, onClick, isAdmin, hoverBorder = 'hover:border-acid', participantHrefs = {} }) {
  const canEdit = isAdmin && match.participant1_id && match.participant2_id
  const isBye = match.score === 'BYE'
  const isNoShow = match.score === 'NO-SHOW' || match.score === 'NO-CONTEST'

  const statusChip = () => {
    if (isBye) return <span className="text-[10px] text-muted-foreground uppercase tracking-wider">BYE</span>
    if (isNoShow) return <span className="text-[10px] text-signal uppercase tracking-wider">{match.score}</span>
    switch (match.status) {
      case 'completed':  return <CheckCircle2 className="h-3 w-3 text-live" />
      case 'in_progress': return <Clock className="h-3 w-3 text-live" />
      default: return <Circle className="h-3 w-3 text-muted-foreground" />
    }
  }

  // Score wordt bewaard als "N-M"-string; hier parsen zodat we per team het
  // getal naast de naam kunnen tonen (duidelijker dan alleen onderaan).
  const [scoreP1, scoreP2] = (() => {
    if (!match.score || isBye || isNoShow) return [null, null]
    const m = String(match.score).match(/^(\d+)\s*[-:]\s*(\d+)$/)
    return m ? [m[1], m[2]] : [null, null]
  })()

  return (
    <Card
      className={cn(
        'h-full transition-colors overflow-hidden',
        canEdit && `cursor-pointer ${hoverBorder}`,
        match.status === 'in_progress' && 'border-live/60 shadow-[0_0_0_1px_rgb(var(--live-rgb)/0.4)]'
      )}
      onClick={() => canEdit && onClick && onClick()}
    >
      <CardContent className="p-3 h-full flex flex-col gap-1.5">
        <div className="flex items-center justify-between text-[11px] font-mono uppercase tracking-widest text-muted-foreground">
          <span>
            {match.is_third_place_match ? 'Brons' : `Match ${match.round}.${match.match_number}`}
            {Number(match.best_of || 1) > 1 && (
              <span className="ml-2 text-acid">BO{match.best_of}</span>
            )}
            {match.map && (
              <span className="ml-2 text-live" title="Map (via veto)">{match.map}</span>
            )}
          </span>
          <span className="flex items-center gap-1">{statusChip()}</span>
        </div>
        <div className="flex-1 flex flex-col gap-1 justify-center min-h-0">
          <ParticipantRow name={match.participant1_name}
                          href={participantHrefs[match.participant1_id]}
                          isWinner={!!match.winner_id && match.winner_id === match.participant1_id}
                          isNull={!match.participant1_id}
                          score={scoreP1} />
          <ParticipantRow name={match.participant2_name}
                          href={participantHrefs[match.participant2_id]}
                          isWinner={!!match.winner_id && match.winner_id === match.participant2_id}
                          isNull={!match.participant2_id}
                          score={scoreP2} />
        </div>
        {/* BYE/NO-SHOW/NO-CONTEST label onderaan want die krijgen geen per-team getal */}
        {match.score && (isBye || isNoShow) && (
          <div className="text-center text-xs font-mono uppercase tracking-widest text-muted-foreground pt-1 border-t border-border/40">
            {match.score}
          </div>
        )}
        {/* BO-series: toon per-game scores compact onderaan */}
        {match.game_scores && !isBye && !isNoShow && (
          <div className="text-center text-[10px] font-mono tabular-nums text-muted-foreground pt-1 border-t border-border/40 truncate">
            {match.game_scores}
          </div>
        )}
      </CardContent>
    </Card>
  )
}

/* Herbruikbaar: klikbare speler-/team-naam voor alle bracket-varianten.
   Als er geen href is (bv. voor niet-persistente teams of TBD), toon plain text. */
export function ParticipantName({ name, href, className, ...rest }) {
  if (!name) return null
  if (!href) return <span className={className} {...rest}>{name}</span>
  return (
    <Link to={href} target="_blank" rel="noopener noreferrer"
          onClick={(e) => e.stopPropagation()}
          title="Open profiel in nieuw tabblad"
          className={cn('hover:text-acid hover:underline decoration-dotted', className)}
          {...rest}>
      {name}
    </Link>
  )
}

function ParticipantRow({ name, href, isWinner, isNull, score }) {
  const label = name || (isNull ? '—' : 'TBD')
  return (
    <div className={cn(
      'flex items-center gap-2 px-2.5 py-1.5 rounded-md text-sm',
      isWinner ? 'bg-live/15 text-live font-semibold ring-1 ring-live/40'
               : isNull ? 'bg-muted/20 text-muted-foreground italic'
                        : 'bg-card/60 border border-border/60'
    )}>
      <ParticipantName name={label} href={href} className="truncate flex-1" />
      {score != null && (
        <span className={cn(
          'font-mono tabular-nums text-sm font-bold shrink-0 min-w-[1.5rem] text-right',
          isWinner ? 'text-live' : 'text-muted-foreground'
        )}>{score}</span>
      )}
      {isWinner && <Trophy className="h-3.5 w-3.5 text-live shrink-0" />}
    </div>
  )
}
